import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Moon, Sun, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AuthBrandLogo from '../components/AuthBrandLogo';
import logoIconLight from '../assets/logo-icon.png';
import logoIconDark from '../assets/logo-icon-dark.png';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const logoIconSrc = theme === 'dark' ? logoIconDark : logoIconLight;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.requires_email_verification) {
        if (res.email_sent === false) {
          toast.error('Could not send verification email. Use Resend code on the next screen after fixing server config.');
        } else {
          toast.success('We sent a verification code to your email.');
        }
        navigate(
          `/verify-email?email=${encodeURIComponent(res.email || email)}&verification_id=${encodeURIComponent(res.verification_id || '')}`
        );
        return;
      }
      if (res?.requires_2fa) {
        if (res.email_sent === false) {
          toast.error('Could not send login code. Use Resend code on the next screen.');
        } else {
          toast.success('We sent a login code to your email.');
        }
        navigate(
          `/login-otp?email=${encodeURIComponent(res.email || email)}&challenge_id=${encodeURIComponent(res.challenge_id || '')}`
        );
        return;
      }
      toast.success('Welcome back!');
      const mode = localStorage.getItem('superAdminViewMode');
      navigate(mode === 'super_admin' ? '/dashboard/super-admin/overview' : '/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4">
        <AuthBrandLogo />
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle"
          className="text-slate-600 dark:text-slate-400"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-slide-up">
          <Card className="border-slate-200 dark:border-slate-800 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <img
                src={logoIconSrc}
                alt="DocTree"
                className="mx-auto h-12 w-12 mb-2"
              />
              <CardTitle className="text-2xl font-bold font-[Manrope] text-slate-900 dark:text-white">
                Welcome back
              </CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-400">
                Sign in to your DocTree account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm">
                <span className="text-slate-600 dark:text-slate-400">Don't have an account? </span>
                <Link
                  to="/register"
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  data-testid="register-link"
                >
                  Sign up
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-sm text-slate-500 dark:text-slate-500">
        <p>&copy; {new Date().getFullYear()} DocTree. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LoginPage;
